import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Download as DownloadIcon, LocationOn, Visibility } from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import theme from "../theme/theme";
import Table from "../components/Table";
import {
  exportAttendanceSummary,
  fetchAttendanceSummary,
} from "../api/AttendanceService";
import { fetchEmployees } from "../api/EmployeeService";

const DESIGNATION_OPTIONS = [
  { value: "", label: "All Users" },
  { value: "sales", label: "Sales" },
  { value: "service", label: "Service" },
  { value: "engineer", label: "Engineer" },
  { value: "sub_admin", label: "Sub Admin" },
  { value: "others", label: "Others" },
];

const BASE_URL = "https://api.rainbowlifts.com";

const formatTime = (datetime) => {
  if (!datetime) return "-";
  return new Date(datetime).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const statusColor = (status) => {
  switch (status) {
    case "present":
      return "success";
    case "half_day":
      return "warning";
    case "pending":
      return "info";
    case "absent":
      return "error";
    default:
      return "default";
  }
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const firstOfMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const AttendanceSummaryPage = () => {
  const [designation, setDesignation] = useState("");
  const [fromDate, setFromDate] = useState(firstOfMonthStr());
  const [toDate, setToDate] = useState(todayStr());
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeeHasMore, setEmployeeHasMore] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const employeeSearchTimer = useRef(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);

  const [viewRow, setViewRow] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const loadEmployees = async (page, search, append = false) => {
    setLoadingEmployees(true);
    try {
      const res = await fetchEmployees(page, search || "");
      const paginator = res.data ?? {};
      const list = (Array.isArray(paginator.data) ? paginator.data : []).map(
        (e) => ({ id: e.id, label: e.name })
      );
      setEmployeeOptions((prev) => {
        if (!append) return list;
        const seen = new Set(prev.map((o) => o.id));
        return [...prev, ...list.filter((o) => !seen.has(o.id))];
      });
      setEmployeePage(paginator.current_page ?? page);
      setEmployeeHasMore(
        (paginator.current_page ?? page) < (paginator.last_page ?? 1)
      );
    } catch {
      // silent
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleEmployeeInputChange = (event, value, reason) => {
    if (reason !== "input") return;
    setEmployeeSearch(value);
    if (employeeSearchTimer.current) clearTimeout(employeeSearchTimer.current);
    employeeSearchTimer.current = setTimeout(
      () => loadEmployees(1, value),
      400
    );
  };

  const handleEmployeeListScroll = (event) => {
    const el = event.currentTarget;
    if (
      employeeHasMore &&
      !loadingEmployees &&
      el.scrollTop + el.clientHeight >= el.scrollHeight - 48
    ) {
      loadEmployees(employeePage + 1, employeeSearch, true);
    }
  };

  const loadSummary = async () => {
    if (!fromDate || !toDate) return;
    if (new Date(fromDate) > new Date(toDate)) {
      toast.error("From date must be before To date");
      return;
    }
    setLoading(true);
    try {
      const response = await fetchAttendanceSummary({
        from_date: fromDate,
        to_date: toDate,
        designation: designation || undefined,
        employee_ids: selectedEmployees.map((e) => e.id),
      });
      setRows(response.data?.summary || []);
    } catch (error) {
      toast.error(error.message || "Failed to fetch attendance summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees(1, "");
    return () => {
      if (employeeSearchTimer.current)
        clearTimeout(employeeSearchTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designation, fromDate, toDate, selectedEmployees]);

  const handleExport = async () => {
    if (!fromDate || !toDate) return;
    if (new Date(fromDate) > new Date(toDate)) {
      toast.error("From date must be before To date");
      return;
    }
    setExporting(true);
    try {
      const response = await exportAttendanceSummary({
        from_date: fromDate,
        to_date: toDate,
        designation: designation || undefined,
        employee_ids: selectedEmployees.map((e) => e.id),
      });
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `attendance-summary-${fromDate}-to-${toDate}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error(error?.message || "Failed to export attendance summary");
    } finally {
      setExporting(false);
    }
  };

  const tableData = useMemo(
    () =>
      rows.map((r, idx) => ({
        id: `${r.employee_id}-${r.date}-${idx}`,
        ...r,
      })),
    [rows],
  );

  const totalRows = tableData.length;
  const lastPage = Math.max(1, Math.ceil(totalRows / perPage));
  const safePage = Math.min(currentPage, lastPage);
  const pagedData = useMemo(
    () => tableData.slice((safePage - 1) * perPage, safePage * perPage),
    [tableData, safePage, perPage],
  );
  const meta = {
    from: totalRows === 0 ? 0 : (safePage - 1) * perPage + 1,
    to: Math.min(safePage * perPage, totalRows),
    total: totalRows,
  };
  const buildLinks = () => {
    if (totalRows === 0) return [];
    const links = [
      { label: "&laquo; Previous", page: safePage - 1, active: false, url: safePage > 1 ? "x" : null },
    ];
    for (let i = 1; i <= lastPage; i++) {
      links.push({ label: String(i), page: i, active: i === safePage, url: "x" });
    }
    links.push({
      label: "Next &raquo;",
      page: safePage + 1,
      active: false,
      url: safePage < lastPage ? "x" : null,
    });
    return links;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [perPage, rows]);

  const columns = [
    { field: "employee_name", headerName: "Employee" },
    {
      field: "designation",
      headerName: "Type",
      render: (row) =>
        row.designation
          ? row.designation.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())
          : "-",
    },
    {
      field: "date",
      headerName: "Date",
      render: (row) => formatDate(row.date),
    },
    {
      field: "first_in",
      headerName: "First In",
      render: (row) => formatTime(row.first_in),
    },
    {
      field: "last_out",
      headerName: "Last Out",
      render: (row) => formatTime(row.last_out),
    },
    { field: "session_count", headerName: "Sessions" },
    {
      field: "total_hours",
      headerName: "Total Hours",
      render: (row) => row.total_hours || "-",
    },
    {
      field: "status",
      headerName: "Status",
      render: (row) => (
        <Chip
          label={row.status}
          color={statusColor(row.status)}
          size="small"
          sx={{ textTransform: "capitalize" }}
        />
      ),
    },
  ];

  const actions = [
    {
      icon: <Visibility fontSize="small" />,
      label: "View",
      onClick: (row) => setViewRow(row),
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          Attendance Summary
        </Box>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={exporting || loading || rows.length === 0}
        >
          {exporting ? "Exporting..." : "Export CSV"}
        </Button>
      </Box>

      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: theme.paper.borderRadius,
          backgroundColor: theme.paper.backgroundColor,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 220 }}>
            <Typography
              variant="body2"
              color={theme.palette.primary.main}
              fontWeight="bold"
            >
              User Type
            </Typography>
            <TextField
              select
              size="small"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
            >
              {DESIGNATION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 300 }}>
            <Typography
              variant="body2"
              color={theme.palette.primary.main}
              fontWeight="bold"
            >
              Employee(s)
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={employeeOptions}
              loading={loadingEmployees}
              value={selectedEmployees}
              onChange={(_, newValue) => setSelectedEmployees(newValue)}
              onInputChange={handleEmployeeInputChange}
              filterOptions={(x) => x}
              ListboxProps={{ onScroll: handleEmployeeListScroll }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.label}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id}
                    label={option.label}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={selectedEmployees.length === 0 ? "All employees" : ""}
                />
              )}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 180 }}>
            <Typography
              variant="body2"
              color={theme.palette.primary.main}
              fontWeight="bold"
            >
              From Date
            </Typography>
            <TextField
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 180 }}>
            <Typography
              variant="body2"
              color={theme.palette.primary.main}
              fontWeight="bold"
            >
              To Date
            </Typography>
            <TextField
              type="date"
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Button
            variant="outlined"
            onClick={() => {
              setDesignation("");
              setFromDate(firstOfMonthStr());
              setToDate(todayStr());
              setSelectedEmployees([]);
            }}
          >
            Reset
          </Button>
        </Box>
      </Paper>

      <Table
        columns={columns}
        data={pagedData}
        loading={loading}
        actions={actions}
        links={buildLinks()}
        setCurrentPage={setCurrentPage}
        meta={meta}
        perPage={perPage}
        setPerPage={setPerPage}
      />

      {!loading && tableData.length === 0 && (
        <Paper
          sx={{
            mt: 2,
            p: 4,
            textAlign: "center",
            borderRadius: theme.paper.borderRadius,
            backgroundColor: theme.paper.backgroundColor,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No attendance records for the selected filters.
          </Typography>
        </Paper>
      )}

      {/* View Dialog — all check-ins/outs for the day */}
      <Dialog
        open={!!viewRow}
        onClose={() => setViewRow(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {viewRow?.employee_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {viewRow ? formatDate(viewRow.date) : ""}
              {viewRow ? ` · ${viewRow.session_count} session${viewRow.session_count > 1 ? "s" : ""}` : ""}
              {viewRow?.total_hours ? ` · ${viewRow.total_hours}` : ""}
            </Typography>
          </Box>
          <IconButton onClick={() => setViewRow(null)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {viewRow?.logs && viewRow.logs.length > 0 ? (
            <TableContainer>
              <MuiTable size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "rgba(0,0,0,0.03)" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>Sr. No</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Check In</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Check Out</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Worked</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>In Photo</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Out Photo</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>In Location</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Out Location</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewRow.logs.map((record, index) => (
                    <TableRow key={record.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{formatTime(record.time_in)}</TableCell>
                      <TableCell>{formatTime(record.time_out)}</TableCell>
                      <TableCell>
                        {record.total_minutes
                          ? `${Math.floor(record.total_minutes / 60)}h ${record.total_minutes % 60}m`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {record.photo_in ? (
                          <Box
                            component="img"
                            src={`${BASE_URL}/${record.photo_in}`}
                            alt="Check In"
                            onClick={() =>
                              setPreviewImage(`${BASE_URL}/${record.photo_in}`)
                            }
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              objectFit: "cover",
                              cursor: "pointer",
                              "&:hover": { opacity: 0.8 },
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {record.photo_out ? (
                          <Box
                            component="img"
                            src={`${BASE_URL}/${record.photo_out}`}
                            alt="Check Out"
                            onClick={() =>
                              setPreviewImage(`${BASE_URL}/${record.photo_out}`)
                            }
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              objectFit: "cover",
                              cursor: "pointer",
                              "&:hover": { opacity: 0.8 },
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {record.latitude_in && record.longitude_in ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              cursor: "pointer",
                              "&:hover": { opacity: 0.7 },
                            }}
                            onClick={() =>
                              window.open(
                                `https://www.google.com/maps?q=${record.latitude_in},${record.longitude_in}`,
                                "_blank",
                              )
                            }
                          >
                            <LocationOn fontSize="small" color="primary" />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.65rem", lineHeight: 1.2 }}
                            >
                              {Number(record.latitude_in).toFixed(4)},
                              <br />
                              {Number(record.longitude_in).toFixed(4)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {record.latitude_out && record.longitude_out ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              cursor: "pointer",
                              "&:hover": { opacity: 0.7 },
                            }}
                            onClick={() =>
                              window.open(
                                `https://www.google.com/maps?q=${record.latitude_out},${record.longitude_out}`,
                                "_blank",
                              )
                            }
                          >
                            <LocationOn fontSize="small" color="error" />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.65rem", lineHeight: 1.2 }}
                            >
                              {Number(record.latitude_out).toFixed(4)},
                              <br />
                              {Number(record.longitude_out).toFixed(4)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </MuiTable>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No logs found
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Image preview dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden", position: "relative" },
        }}
      >
        <IconButton
          onClick={() => setPreviewImage(null)}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: "rgba(0,0,0,0.5)",
            color: "#fff",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
          }}
          size="small"
        >
          <Close fontSize="small" />
        </IconButton>
        {previewImage && (
          <Box
            component="img"
            src={previewImage}
            alt="Preview"
            sx={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default AttendanceSummaryPage;
